"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import { Appointment } from "./appointment-card"

type NoosaAppointmentCardsProps = {
  appointments: Appointment[]
  onBook: (source?: string) => void
}

export function NoosaAppointmentCards({ appointments, onBook }: NoosaAppointmentCardsProps) {
  const [flippedByIndex, setFlippedByIndex] = useState<Record<number, boolean>>({})

  const setFlipped = (index: number, value: boolean) => {
    setFlippedByIndex((prev) => ({ ...prev, [index]: value }))
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto place-items-center md:place-items-stretch">
      {appointments.map((appt, index) => (
        <motion.div
          key={`${appt.section}-${appt.title}`}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: index * 0.1 }}
          className="w-full aspect-square relative"
          style={{ perspective: 1000 }}
        >
          <motion.div
            className="absolute inset-0"
            style={{ transformStyle: "preserve-3d" as React.CSSProperties["transformStyle"] }}
            animate={{ rotateY: flippedByIndex[index] ? 180 : 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          >
            {/* Front */}
            <div className="absolute inset-0" style={{ backfaceVisibility: "hidden" as React.CSSProperties["backfaceVisibility"] }}>
              <Card className="h-full flex flex-col relative rounded-3xl border border-gray-200 shadow">
                {/* Image header with overlays */}
                <div className="relative h-40 md:h-48 rounded-t-3xl overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: appt.image ? `url(${appt.image})` : undefined as any }}
                  />
                  {!appt.image && (
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300" />
                  )}
                  {/* Price/duration pill */}
                  <div className="absolute top-4 right-4 px-4 py-2 rounded-xl bg-neutral-500/40 backdrop-blur-sm text-white text-sm md:text-base font-semibold">
                    {appt.price} <span className="opacity-80">|</span> {appt.duration}
                  </div>
                  {/* Book button in image area */}
                  <button 
                    onClick={() => onBook('card')} 
                    className="absolute right-4 bottom-4 bg-white text-gray-800 rounded-xl px-5 py-2 text-sm md:text-base font-medium hover:bg-gray-100 transition-colors"
                  >
                    Book Appointment
                  </button>
                </div>
                <CardContent className="flex-1 flex flex-col gap-2 pt-4">
                  <h4 className="text-gray-700 font-normal text-lg">{appt.title}</h4>
                  <p className="text-gray-600 leading-relaxed text-sm">{appt.frontDescription}</p>
                  <div className="mt-auto text-right">
                    <button 
                      onClick={() => setFlipped(index, true)} 
                      className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      Read more…
                    </button>
                  </div>
                </CardContent>
              </Card>
            </div>
            {/* Back */}
            <div className="absolute inset-0" style={{ backfaceVisibility: "hidden" as React.CSSProperties["backfaceVisibility"], transform: "rotateY(180deg)" }}>
              <Card className="h-full flex flex-col relative rounded-3xl border border-gray-200 shadow">
                <button 
                  onClick={() => setFlipped(index, false)} 
                  className="absolute top-3 right-3 text-xs uppercase tracking-wide px-2 py-1 rounded-md bg-black/25 hover:bg-black/40 text-white transition-colors z-10"
                >
                  Back
                </button>
                <CardContent className="flex-1 overflow-hidden pt-8">
                  <div className="h-full overflow-y-auto pr-1 space-y-4 text-gray-600 text-sm">
                    {appt.backDetails.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </motion.div>
      ))}
    </div>
  )
}

